import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  analyzeCampaignBuilder,
  commitCampaignBuilder,
  getCampaignBuilderInventory,
  type CampaignDraftProposal,
} from "../api/campaigns";

interface Props {
  open?: boolean;
  onClose: () => void;
  onCreated: (campaignId: number) => void;
  onOpenClassicForm?: (draft: CampaignDraftProposal) => void;
}

type RecipeId =
  | "press1_rescue"
  | "press1"
  | "press1_instant_sms"
  | "direct"
  | "broadcast";

type TransferMode = "extension" | "sip" | "phone" | "agents";

const RECIPES: {
  id: RecipeId;
  badge: string;
  title: string;
  subtitle: string;
  campaignType: "press1" | "direct" | "broadcast";
  rescueSms: boolean;
  instantSms: boolean;
}[] = [
  {
    id: "press1_rescue",
    badge: "Most Popular",
    title: "Press-Key Transfer + Non-Convert Callback SMS",
    subtitle:
      "Play greeting → Press 1 or 3 to transfer → If they don't convert, text them a Toll-Free number to your inbound queue.",
    campaignType: "press1",
    rescueSms: true,
    instantSms: false,
  },
  {
    id: "press1",
    badge: "Standard IVR",
    title: "Press-1 / Press-3 Transfer Only",
    subtitle:
      "Play greeting → Press 1 or 3 to transfer to your SIP endpoint, phone number, or agent queue (Press 9 = DNC).",
    campaignType: "press1",
    rescueSms: false,
    instantSms: false,
  },
  {
    id: "press1_instant_sms",
    badge: "Instant Text",
    title: "Press-Key Transfer + Instant Text on Keypress",
    subtitle:
      "When they press 1 or 3, transfer them AND immediately text them your callback number or link.",
    campaignType: "press1",
    rescueSms: false,
    instantSms: true,
  },
  {
    id: "direct",
    badge: "No Press-1 • Instant Rep",
    title: "Direct to Rep (When Customer Picks Up → Connect Right to Rep)",
    subtitle:
      "No Press-1 prompt — the moment a live customer answers, connect them immediately to your rep (with optional voicemail drop or hang up on machines).",
    campaignType: "direct",
    rescueSms: false,
    instantSms: false,
  },
  {
    id: "broadcast",
    badge: "No Transfer",
    title: "Voice Broadcast + Voicemail Drop",
    subtitle:
      "Play an audio message to live pickups and leave a voicemail on answering machines.",
    campaignType: "broadcast",
    rescueSms: false,
    instantSms: false,
  },
];

function buildSmsTemplate(
  preset: "queue" | "debt" | "tax",
  tollFreeFormatted: string,
): string {
  const num = tollFreeFormatted || "1-888-555-0199";
  if (preset === "debt") {
    return `Debt Relief Specialist Desk: In case we were disconnected or you are ready to finish reviewing your balances for the single lower monthly payment plan, call your specialist directly at ${num}. Reply STOP to opt out.`;
  }
  if (preset === "tax") {
    return `Tax Resolution Desk: In case we were disconnected before completing your relief review, call your assigned specialist toll-free at ${num}. Reply STOP to opt out.`;
  }
  return `Specialist Desk: In case we were disconnected before finishing your call, reach our priority inbound queue directly toll-free at ${num}. Reply STOP to opt out.`;
}

function replacePhoneInSms(text: string, newFormattedPhone: string): string {
  if (!text || !newFormattedPhone) return text;
  const replaced = text.replace(
    /\+?1?[-.\s()]?\d{3}[-.\s()]?\d{3}[-.\s]?\d{4}/g,
    newFormattedPhone,
  );
  return replaced;
}

export default function CampaignAiBuilderModal({
  open = true,
  onClose,
  onCreated,
  onOpenClassicForm,
}: Props) {
  const [recipe, setRecipe] = useState<RecipeId>("press1_rescue");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);

  // Step 1: List, DIDs, Caller ID Pool Callback Forwarding, Channels
  const [contactGroupId, setContactGroupId] = useState<number | null>(null);
  const [didGroupId, setDidGroupId] = useState<number | null>(null);
  const [didPoolForwardMode, setDidPoolForwardMode] = useState<
    "same_as_transfer" | "keep" | "custom"
  >("same_as_transfer");
  const [didPoolForwardTarget, setDidPoolForwardTarget] = useState<string>("");
  const [didPoolInboundMode, setDidPoolInboundMode] = useState<
    "direct" | "press1" | "press3"
  >("press1");
  const [didPoolInboundTouched, setDidPoolInboundTouched] =
    useState<boolean>(false);
  const [channel, setChannel] = useState<number>(25);
  const ringTimeSec = 30;
  const tryAllowed = 1;

  // Step 2: Greeting, Keypress, Voicemail
  const [recordingFile, setRecordingFile] = useState<string>("");
  const [amRecordingFile, setAmRecordingFile] = useState<string>("__same__");
  const [connectKey, setConnectKey] = useState<string>("3");
  const [optoutKey, setOptoutKey] = useState<string>("9");

  // Step 3: Transfer Target
  const [transferMode, setTransferMode] = useState<TransferMode>("extension");
  const [transferExtensionId, setTransferExtensionId] = useState<number | null>(
    null,
  );
  const [customSipUri, setCustomSipUri] = useState<string>("");
  const [customPhone, setCustomPhone] = useState<string>("");

  // Step 4: Non-Conversion Follow-Up SMS & Toll-Free Queue Routing
  const [rescueEnabled, setRescueEnabled] = useState<boolean>(true);
  const [minConvertSec, setMinConvertSec] = useState<number>(300);
  const [callbackDidId, setCallbackDidId] = useState<number | null>(null);
  const [autoBuyTollFree, setAutoBuyTollFree] = useState<boolean>(false);
  const [callbackRouteMode, setCallbackRouteMode] = useState<
    "same_as_transfer" | "keep" | "inbound_queue" | "custom"
  >("same_as_transfer");
  const [callbackCustomRouteTarget, setCallbackCustomRouteTarget] =
    useState<string>("");
  const [rescueSmsText, setRescueSmsText] = useState<string>("");
  const [rescueSmsFrom, setRescueSmsFrom] = useState<string>("");

  // Optional Instant SMS on Keypress
  const [instantSmsEnabled, setInstantSmsEnabled] = useState<boolean>(false);
  const [instantSmsText, setInstantSmsText] = useState<string>("");

  // Optional Plain-English Quick Auto-Fill bar
  const [showAutoFillBar, setShowAutoFillBar] = useState<boolean>(false);
  const [autoFillPrompt, setAutoFillPrompt] = useState<string>("");
  const [autoFillBanner, setAutoFillBanner] = useState<string | null>(null);
  const [autopilotNotes, setAutopilotNotes] = useState<string[]>([]);
  const [initializedFromBlueprint, setInitializedFromBlueprint] =
    useState<boolean>(false);

  // Load operator inventory on open
  const invQuery = useQuery({
    queryKey: ["campaign-builder-inventory"],
    queryFn: getCampaignBuilderInventory,
    enabled: open,
    staleTime: 15_000,
  });

  const inv = invQuery.data;

  // Initialize from the backend's self-healed `last_campaign_blueprint` once inventory loads
  useEffect(() => {
    if (!open || !inv || initializedFromBlueprint) return;
    setInitializedFromBlueprint(true);

    const bp = inv.last_campaign_blueprint;
    if (inv.self_healed_notes?.length) {
      setAutopilotNotes(inv.self_healed_notes);
    }

    if (bp) {
      if (bp.recipe && RECIPES.some((r) => r.id === bp.recipe)) {
        setRecipe(bp.recipe as RecipeId);
      }
      if (bp.contact_group_id) {
        setContactGroupId(bp.contact_group_id);
      } else if (inv.contact_groups.length > 0) {
        setContactGroupId(inv.contact_groups[0].id);
      }
      if (bp.did_group_id) {
        setDidGroupId(bp.did_group_id);
      } else if (inv.did_groups.length > 0) {
        setDidGroupId(inv.did_groups[0].id);
      }
      if (
        bp.did_pool_forward_mode &&
        ["same_as_transfer", "keep", "custom"].includes(bp.did_pool_forward_mode)
      ) {
        setDidPoolForwardMode(bp.did_pool_forward_mode);
      }
      if (bp.did_pool_forward_target) {
        setDidPoolForwardTarget(bp.did_pool_forward_target);
      }
      if (
        bp.did_pool_inbound_mode &&
        ["direct", "press1", "press3"].includes(bp.did_pool_inbound_mode)
      ) {
        setDidPoolInboundMode(bp.did_pool_inbound_mode);
      } else if (bp.campaign_type === "direct") {
        setDidPoolInboundMode("direct");
      } else if (inv.did_groups[0]?.inbound_mode) {
        setDidPoolInboundMode(inv.did_groups[0].inbound_mode);
      } else if (bp.connect_key === "3") {
        setDidPoolInboundMode("press3");
      }
      if (bp.channel && bp.channel >= 1) {
        setChannel(bp.channel);
      }
      if (bp.recording_file) {
        setRecordingFile(bp.recording_file);
      } else if (inv.recordings.length > 0) {
        setRecordingFile(inv.recordings[0].ref);
      }
      if (bp.am_recording_file && bp.am_recording_file !== bp.recording_file) {
        setAmRecordingFile(bp.am_recording_file);
      } else {
        const vmRec = inv.recordings.find((r) => r.kind === "voicemail");
        setAmRecordingFile(vmRec ? vmRec.ref : "__same__");
      }
      if (bp.connect_key) {
        setConnectKey(bp.connect_key);
      }
      if (bp.optout_key) {
        setOptoutKey(bp.optout_key);
      }
      if (bp.transfer_target === "agents") {
        setTransferMode("agents");
      } else if (bp.transfer_extension_id) {
        setTransferExtensionId(bp.transfer_extension_id);
        setTransferMode("extension");
      } else if (inv.extensions.length > 0) {
        setTransferExtensionId(inv.extensions[0].id);
        setTransferMode("extension");
      } else {
        setTransferMode("sip");
      }

      setRescueEnabled(Boolean(bp.callback_rescue_enabled));
      if (bp.cta_target_min_duration_sec && bp.cta_target_min_duration_sec >= 30) {
        setMinConvertSec(bp.cta_target_min_duration_sec);
      }
      if (bp.callback_did_id) {
        setCallbackDidId(bp.callback_did_id);
        setAutoBuyTollFree(false);
      } else if (inv.callback_dids?.[0]) {
        setCallbackDidId(inv.callback_dids[0].id);
        setAutoBuyTollFree(false);
      } else {
        setCallbackDidId(null);
        setAutoBuyTollFree(true);
      }
      if (bp.callback_rescue_sms_from) {
        setRescueSmsFrom(bp.callback_rescue_sms_from);
      } else if (inv.sms_defaults?.callback_rescue_sms_from) {
        setRescueSmsFrom(inv.sms_defaults.callback_rescue_sms_from);
      }
      const tfPhone =
        bp.callback_did_phone ||
        inv.callback_dids?.[0]?.formatted ||
        "1-833-XXX-XXXX";
      if (bp.callback_rescue_sms_text) {
        setRescueSmsText(replacePhoneInSms(bp.callback_rescue_sms_text, tfPhone));
      } else if (inv.sms_defaults?.callback_rescue_sms_text) {
        setRescueSmsText(
          replacePhoneInSms(inv.sms_defaults.callback_rescue_sms_text, tfPhone),
        );
      } else {
        setRescueSmsText(buildSmsTemplate("queue", tfPhone));
      }
      return;
    }

    if (contactGroupId === null && inv.contact_groups.length > 0) {
      setContactGroupId(inv.contact_groups[0].id);
    }
    if (didGroupId === null && inv.did_groups.length > 0) {
      setDidGroupId(inv.did_groups[0].id);
      if (inv.did_groups[0].inbound_mode) {
        setDidPoolInboundMode(inv.did_groups[0].inbound_mode);
      }
    }
    if (!recordingFile && inv.recordings.length > 0) {
      const firstRec = inv.recordings[0];
      setRecordingFile(firstRec.ref);
      if (firstRec.detected_key) {
        setConnectKey(firstRec.detected_key);
      }
      if (firstRec.detected_optout_key) {
        setOptoutKey(firstRec.detected_optout_key);
      }
    }
    if (transferExtensionId === null && inv.extensions.length > 0) {
      setTransferExtensionId(inv.extensions[0].id);
      setTransferMode("extension");
    } else if (inv.extensions.length === 0) {
      setTransferMode("sip");
    }

    // Default Toll-Free / Callback DID + SMS defaults
    const topDid = inv.callback_dids?.[0];
    if (topDid) {
      setCallbackDidId(topDid.id);
      setAutoBuyTollFree(false);
    } else {
      setCallbackDidId(null);
      setAutoBuyTollFree(true);
    }
    const defaultDur = inv.sms_defaults?.cta_target_min_duration_sec;
    if (defaultDur && defaultDur >= 30) {
      setMinConvertSec(defaultDur);
    }
    if (!rescueSmsFrom && inv.sms_defaults?.callback_rescue_sms_from) {
      setRescueSmsFrom(inv.sms_defaults.callback_rescue_sms_from);
    }
    if (!rescueSmsText) {
      const tfPhone = topDid?.formatted || "1-833-XXX-XXXX";
      if (inv.sms_defaults?.callback_rescue_sms_text) {
        setRescueSmsText(
          replacePhoneInSms(inv.sms_defaults.callback_rescue_sms_text, tfPhone),
        );
      } else {
        setRescueSmsText(buildSmsTemplate("queue", tfPhone));
      }
    }
  }, [open, inv, initializedFromBlueprint]);

  // Select recipe handler
  const handleSelectRecipe = (rId: RecipeId) => {
    setRecipe(rId);
    const meta = RECIPES.find((r) => r.id === rId)!;
    setRescueEnabled(meta.rescueSms);
    setInstantSmsEnabled(meta.instantSms);
    if (!didPoolInboundTouched) {
      if (meta.campaignType === "direct") {
        setDidPoolInboundMode("direct");
      } else if (selectedDidGroup?.inbound_mode && selectedDidGroup.inbound_mode !== "direct") {
        setDidPoolInboundMode(selectedDidGroup.inbound_mode);
      } else if (connectKey === "3") {
        setDidPoolInboundMode("press3");
      } else {
        setDidPoolInboundMode("press1");
      }
    }
    if (meta.instantSms && !instantSmsText) {
      const tfPhone = selectedCallbackPhone || "1-888-555-0199";
      setInstantSmsText(
        `Thanks for pressing ${connectKey}! We are connecting you now. Keep our priority callback number handy: ${tfPhone}. Reply STOP to opt out.`,
      );
    }
  };

  const activeRecipeMeta = useMemo(
    () => RECIPES.find((r) => r.id === recipe) || RECIPES[0],
    [recipe],
  );
  const campaignType = activeRecipeMeta.campaignType;

  const selectedList = useMemo(
    () => inv?.contact_groups.find((g) => g.id === contactGroupId) || null,
    [inv, contactGroupId],
  );
  const selectedDidGroup = useMemo(
    () => inv?.did_groups.find((d) => d.id === didGroupId) || null,
    [inv, didGroupId],
  );
  const selectedRecording = useMemo(
    () => inv?.recordings.find((r) => r.ref === recordingFile) || null,
    [inv, recordingFile],
  );
  const selectedExtension = useMemo(
    () => inv?.extensions.find((x) => x.id === transferExtensionId) || null,
    [inv, transferExtensionId],
  );
  const selectedCallbackDid = useMemo(
    () => inv?.callback_dids.find((d) => d.id === callbackDidId) || null,
    [inv, callbackDidId],
  );

  const selectedCallbackPhone = useMemo(() => {
    if (!autoBuyTollFree && callbackDidId && selectedCallbackDid) {
      return selectedCallbackDid.formatted;
    }
    return "Auto-Buy New Toll-Free (1-833 / 1-888)";
  }, [autoBuyTollFree, callbackDidId, selectedCallbackDid]);

  // Auto-generate campaign name unless user edited it manually
  useEffect(() => {
    if (nameTouched) return;
    const typeLabel =
      campaignType === "press1"
        ? `Press-${connectKey}${rescueEnabled ? " + SMS Rescue" : ""}`
        : campaignType === "direct"
          ? "Direct Connect"
          : "Broadcast";
    const listLabel = selectedList?.name || "New Campaign";
    setName(`${typeLabel} — ${listLabel}`.slice(0, 95));
  }, [campaignType, connectKey, rescueEnabled, selectedList, nameTouched]);

  // Check if recording name/transcript mentions a different keypress than connectKey
  const recordingDetectedKey = selectedRecording?.detected_key || null;
  const hasKeyMismatch =
    campaignType === "press1" &&
    Boolean(recordingDetectedKey) &&
    recordingDetectedKey !== connectKey;

  // Compute human-readable transfer destination display
  const transferSummaryDisplay = useMemo(() => {
    if (campaignType === "broadcast") return "None (Broadcast only)";
    if (transferMode === "agents") {
      return "My Logged-In Agents (Campaign Queue)";
    }
    if (transferMode === "extension") {
      if (!selectedExtension) return "Not selected";
      return `${selectedExtension.name} → ${selectedExtension.forward_to || "configured"}`;
    }
    if (transferMode === "sip") {
      const v = customSipUri.trim().replace(/^sip:/i, "");
      return v ? `SIP: ${v}` : "Enter SIP URI";
    }
    if (transferMode === "phone") {
      const v = customPhone.trim();
      if (v.includes("@")) return `SIP: ${v.replace(/^sip:/i, "")}`;
      const d = v.replace(/\D/g, "");
      return d.length >= 10 ? `Phone: ${v}` : "Enter Phone #";
    }
    return "Not selected";
  }, [
    campaignType,
    transferMode,
    selectedExtension,
    customSipUri,
    customPhone,
  ]);

  // Build the CampaignDraftProposal payload for commit
  const draftPayload: CampaignDraftProposal = useMemo(() => {
    let transferTarget: "pstn" | "agents" = "pstn";
    let extId: number | null = null;
    let newForwardTo: string | null = null;
    let newExtName: string | null = null;

    if (campaignType in { press1: 1, direct: 1 }) {
      if (transferMode === "agents") {
        transferTarget = "agents";
      } else if (transferMode === "extension" && selectedExtension) {
        transferTarget = "pstn";
        extId = selectedExtension.id;
      } else if (transferMode === "sip" && customSipUri.trim()) {
        transferTarget = "pstn";
        const raw = customSipUri.trim().replace(/^sip:/i, "");
        if (raw.includes("@")) {
          newForwardTo = raw;
          newExtName = `SIP ${newForwardTo}`.slice(0, 60);
        } else {
          const digits = raw.replace(/\D/g, "");
          newForwardTo = digits.length === 10 ? `1${digits}` : digits;
          newExtName = `Transfer ${raw}`.slice(0, 60);
        }
      } else if (transferMode === "phone" && customPhone.trim()) {
        transferTarget = "pstn";
        const raw = customPhone.trim().replace(/^sip:/i, "");
        if (raw.includes("@")) {
          newForwardTo = raw;
          newExtName = `SIP ${newForwardTo}`.slice(0, 60);
        } else {
          const digits = raw.replace(/\D/g, "");
          newForwardTo = digits.length === 10 ? `1${digits}` : digits;
          newExtName = `Transfer ${customPhone.trim()}`.slice(0, 60);
        }
      }
    }

    let cbRouteMode:
      | "keep"
      | "same_as_transfer"
      | "inbound_queue"
      | "sip_uri"
      | "external_number" = "keep";
    let cbRouteTarget: string | null = null;
    if (callbackRouteMode === "same_as_transfer") {
      cbRouteMode = "same_as_transfer";
    } else if (callbackRouteMode === "inbound_queue") {
      cbRouteMode = "inbound_queue";
      cbRouteTarget = "__agents__";
    } else if (callbackRouteMode === "custom" && callbackCustomRouteTarget.trim()) {
      const t = callbackCustomRouteTarget.trim();
      cbRouteMode = t.includes("@") ? "sip_uri" : "external_number";
      cbRouteTarget = t;
    }

    return {
      name: name.trim() || "New Campaign",
      name_auto_generated: !nameTouched,
      recipe,
      campaign_type: campaignType,
      message_type:
        campaignType === "press1" ? 1 : campaignType === "direct" ? 2 : 0,
      contact_group_id: contactGroupId,
      contact_group_name: selectedList?.name || null,
      contact_group_count: selectedList?.count ?? null,
      did_group_id: didGroupId,
      did_group_name: selectedDidGroup?.name || null,
      did_group_count: selectedDidGroup?.count ?? null,
      did_pool_forward_mode: didPoolForwardMode,
      did_pool_forward_target:
        didPoolForwardMode === "custom" && didPoolForwardTarget.trim()
          ? didPoolForwardTarget.trim()
          : null,
      did_pool_inbound_mode: didPoolInboundMode,
      recording_file:
        campaignType in { press1: 1, broadcast: 1 }
          ? recordingFile || null
          : recordingFile === "__none__"
            ? null
            : amRecordingFile !== "__same__"
              ? amRecordingFile || null
              : recordingFile || null,
      recording_name: selectedRecording?.name || null,
      am_recording_file:
        campaignType === "direct" && recordingFile === "__none__"
          ? null
          : amRecordingFile === "__same__"
            ? recordingFile || null
            : amRecordingFile || recordingFile || null,
      connect_key: connectKey,
      connect_key_confirmed: true,
      optout_key: optoutKey,
      transfer_target: transferTarget,
      transfer_extension_id: extId,
      transfer_destination_display: transferSummaryDisplay,
      new_transfer_forward_to: newForwardTo,
      new_transfer_extension_name: newExtName,
      callback_rescue_enabled:
        campaignType in { press1: 1, direct: 1 } && rescueEnabled,
      cta_target_min_duration_sec: minConvertSec,
      callback_rescue_sms_text: rescueEnabled
        ? rescueSmsText.trim() ||
          buildSmsTemplate("queue", selectedCallbackPhone || "1-888-555-0199")
        : null,
      callback_rescue_sms_from:
        rescueEnabled && rescueSmsFrom.trim() ? rescueSmsFrom.trim() : null,
      callback_did_id: rescueEnabled && !autoBuyTollFree ? callbackDidId : null,
      callback_did_phone:
        rescueEnabled && !autoBuyTollFree && selectedCallbackDid
          ? selectedCallbackDid.formatted
          : null,
      auto_buy_toll_free: rescueEnabled && (autoBuyTollFree || !callbackDidId),
      callback_did_route_mode: cbRouteMode,
      callback_did_route_target: cbRouteTarget,
      press1_sms_enabled:
        campaignType === "press1" && instantSmsEnabled && Boolean(instantSmsText.trim()),
      press1_sms_text:
        campaignType === "press1" && instantSmsEnabled
          ? instantSmsText.trim()
          : null,
      channel,
      ring_time_sec: ringTimeSec,
      try_allowed: tryAllowed,
      local_dial: true,
      am_action:
        campaignType === "direct" && (!recordingFile || recordingFile === "__none__")
          ? "hangup"
          : "leavemessage",
    };
  }, [
    name,
    nameTouched,
    recipe,
    campaignType,
    contactGroupId,
    selectedList,
    didGroupId,
    selectedDidGroup,
    didPoolForwardMode,
    didPoolForwardTarget,
    didPoolInboundMode,
    recordingFile,
    selectedRecording,
    amRecordingFile,
    connectKey,
    optoutKey,
    transferMode,
    selectedExtension,
    customSipUri,
    customPhone,
    transferSummaryDisplay,
    rescueEnabled,
    minConvertSec,
    rescueSmsText,
    rescueSmsFrom,
    callbackDidId,
    autoBuyTollFree,
    selectedCallbackDid,
    selectedCallbackPhone,
    callbackRouteMode,
    callbackCustomRouteTarget,
    instantSmsEnabled,
    instantSmsText,
    channel,
    ringTimeSec,
    tryAllowed,
  ]);

  // Validation checklist (self-healing: accepts SIP or Phone in either custom box, and auto-fills blank SMS)
  const validationErrors = useMemo(() => {
    const errs: string[] = [];
    if (!contactGroupId) errs.push("Select a Contact List to dial");
    if (!didGroupId) errs.push("Select an Outbound Caller ID (DID) Pool");
    if (
      (campaignType === "press1" || campaignType === "broadcast") &&
      (!recordingFile || recordingFile === "__none__")
    ) {
      errs.push("Select a Live Pickup Greeting Recording");
    }
    if (
      didPoolForwardMode === "custom" &&
      !didPoolForwardTarget.trim()
    ) {
      errs.push("Enter a custom SIP URI or Phone Number for Caller ID Pool callbacks");
    }
    if (campaignType === "press1" || campaignType === "direct") {
      if (transferMode === "extension" && !transferExtensionId) {
        errs.push("Select a Saved Transfer Destination");
      } else if (transferMode === "sip" || transferMode === "phone") {
        const raw = (
          transferMode === "sip" ? customSipUri : customPhone
        )
          .trim()
          .replace(/^sip:/i, "");
        const digits = raw.replace(/\D/g, "");
        const validSip = raw.includes("@") && raw.length >= 5;
        const validPhone = digits.length >= 10 && digits.length <= 11;
        if (!validSip && !validPhone) {
          errs.push(
            "Enter a SIP Endpoint (e.g. 13333333331@3.14.99.76) or 10-digit Phone Number",
          );
        }
      }
    }
    return errs;
  }, [
    contactGroupId,
    didGroupId,
    didPoolForwardMode,
    didPoolForwardTarget,
    campaignType,
    recordingFile,
    amRecordingFile,
    transferMode,
    transferExtensionId,
    customSipUri,
    customPhone,
  ]);

  // Plain-English Auto-Fill mutation (optional helper bar)
  const autoFillMut = useMutation({
    mutationFn: (sentence: string) =>
      analyzeCampaignBuilder({
        message: sentence,
        history: [],
        draft: draftPayload,
      }),
    onSuccess: (res) => {
      const d = res.draft;
      if (d.contact_group_id) setContactGroupId(d.contact_group_id);
      if (d.did_group_id) setDidGroupId(d.did_group_id);
      if (d.did_pool_forward_mode) setDidPoolForwardMode(d.did_pool_forward_mode);
      if (d.did_pool_forward_target)
        setDidPoolForwardTarget(d.did_pool_forward_target);
      if (d.did_pool_inbound_mode) {
        setDidPoolInboundMode(d.did_pool_inbound_mode);
        setDidPoolInboundTouched(true);
      }
      if (d.recording_file) setRecordingFile(d.recording_file);
      if (d.connect_key) setConnectKey(d.connect_key);
      if (d.optout_key) setOptoutKey(d.optout_key);
      if (d.channel) setChannel(d.channel);
      if (d.transfer_target === "agents") {
        setTransferMode("agents");
      } else if (d.transfer_extension_id) {
        setTransferMode("extension");
        setTransferExtensionId(d.transfer_extension_id);
      } else if (d.new_transfer_forward_to) {
        if (d.new_transfer_forward_to.includes("@")) {
          setTransferMode("sip");
          setCustomSipUri(d.new_transfer_forward_to);
        } else {
          setTransferMode("phone");
          setCustomPhone(d.new_transfer_forward_to);
        }
      }
      if (d.callback_rescue_enabled) {
        setRecipe("press1_rescue");
        setRescueEnabled(true);
        if (d.cta_target_min_duration_sec) {
          setMinConvertSec(d.cta_target_min_duration_sec);
        }
        if (d.callback_did_id) {
          setCallbackDidId(d.callback_did_id);
        }
        if (d.callback_rescue_sms_text) {
          setRescueSmsText(d.callback_rescue_sms_text);
        }
      } else if (d.campaign_type === "broadcast") {
        setRecipe("broadcast");
        setRescueEnabled(false);
      } else if (d.campaign_type === "direct") {
        setRecipe("direct");
        setRescueEnabled(false);
      }
      setAutoFillBanner(
        "Auto-filled the visual steps below from your description! Review and click Create Campaign.",
      );
    },
  });

  // Commit campaign mutation
  const commitMut = useMutation({
    mutationFn: () => commitCampaignBuilder(draftPayload),
    onSuccess: (res) => {
      onCreated(res.campaign_id);
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm">
      <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 bg-gradient-to-r from-indigo-950/80 via-slate-900 to-slate-900 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/20 text-lg text-indigo-300 ring-1 ring-indigo-500/40">
              ✨
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-white">
                  Easy Campaign Creator
                </h2>
                <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/30">
                  Safe Pending Mode • Zero Mistakes
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Pick a campaign playbook below and fill in the visual steps—transfers, SIP queues, and non-conversion Toll-Free SMS are wired automatically.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAutoFillBar((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-300 hover:bg-indigo-500/20"
            >
              <span>🪄</span>
              {showAutoFillBar ? "Hide Sentence Auto-Fill" : "Paste a Sentence to Auto-Fill"}
            </button>
            {onOpenClassicForm && (
              <button
                type="button"
                onClick={() => onOpenClassicForm(draftPayload)}
                className="rounded-lg border border-slate-700 bg-slate-800/80 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Advanced Form
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2.5 py-1 text-base font-bold text-slate-400 hover:bg-slate-800 hover:text-white"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Optional Collapsible Plain-English Auto-Fill Bar */}
        {showAutoFillBar && (
          <div className="border-b border-indigo-500/30 bg-indigo-950/30 px-6 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="text"
                value={autoFillPrompt}
                onChange={(e) => setAutoFillPrompt(e.target.value)}
                placeholder='Optional: e.g. "Dial my Fresh Leads list with Main DIDs, play Piper intro, if they press 3 transfer to 13333333331@3.14.99.76, if they dont convert text them 888-855-0780"'
                className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
              />
              <button
                type="button"
                disabled={!autoFillPrompt.trim() || autoFillMut.isPending}
                onClick={() => autoFillMut.mutate(autoFillPrompt)}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {autoFillMut.isPending ? "Auto-Filling..." : "🪄 Auto-Fill Steps"}
              </button>
            </div>
            {autoFillBanner && (
              <p className="mt-1.5 text-xs text-emerald-300">
                ✓ {autoFillBanner}
              </p>
            )}
          </div>
        )}

        {/* Body: Left Visual Step Cards (7 cols) + Right Live Flowchart & Create Button (5 cols) */}
        <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-12">
          {/* Left Column: Visual Step-by-Step Builder */}
          <div className="space-y-5 overflow-y-auto p-6 lg:col-span-7">
            {/* Playbook Recipe Selector */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-indigo-300">
                1. Pick Your Campaign Playbook
              </label>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {RECIPES.map((r) => {
                  const active = recipe === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => handleSelectRecipe(r.id)}
                      className={`flex flex-col items-start rounded-xl border p-3.5 text-left transition ${
                        active
                          ? "border-indigo-500 bg-indigo-500/15 shadow-md ring-1 ring-indigo-500/50"
                          : "border-slate-800 bg-slate-950/60 hover:border-slate-700 hover:bg-slate-800/50"
                      } ${r.id === "press1_rescue" ? "sm:col-span-2" : ""}`}
                    >
                      <div className="mb-1 flex w-full items-center justify-between">
                        <span className="text-sm font-bold text-white">
                          {r.title}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            active
                              ? "bg-indigo-500 text-white"
                              : "bg-slate-800 text-slate-400"
                          }`}
                        >
                          {r.badge}
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed text-slate-300">
                        {r.subtitle}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Who to Call & Caller ID Pool */}
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm text-indigo-400">👥</span>
                <h3 className="text-sm font-semibold text-white">
                  2. Who to Call & Outbound Caller ID
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Contact List to Dial
                  </label>
                  <select
                    value={contactGroupId ?? ""}
                    onChange={(e) =>
                      setContactGroupId(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select a contact list...</option>
                    {inv?.contact_groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.count.toLocaleString()} contacts)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    Outbound Caller ID (DID) Pool
                  </label>
                  <select
                    value={didGroupId ?? ""}
                    onChange={(e) => {
                      const nextId = e.target.value
                        ? Number(e.target.value)
                        : null;
                      setDidGroupId(nextId);
                      const grp = inv?.did_groups.find((g) => g.id === nextId);
                      if (grp?.inbound_mode && !didPoolInboundTouched) {
                        setDidPoolInboundMode(grp.inbound_mode);
                      }
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="">Select a DID group...</option>
                    {inv?.did_groups.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name} ({d.count} active DIDs
                        {d.action_data ? ` • → ${d.action_data}` : ""})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Caller ID Pool Callback Routing & Inbound Mode */}
              <div className="mt-3 rounded-lg border border-indigo-500/25 bg-indigo-950/20 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-indigo-300">📲</span>
                    <span className="text-xs font-semibold text-white">
                      When Someone Calls Your Caller ID Back (DID Pool Callback Routing):
                    </span>
                  </div>
                  {selectedDidGroup && (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 ring-1 ring-emerald-500/30">
                      ✓ Guarantees all {selectedDidGroup.count} pool DIDs forward on creation
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-300">
                      Where Caller ID Pool Callbacks Forward To:
                    </label>
                    <select
                      value={didPoolForwardMode}
                      onChange={(e) =>
                        setDidPoolForwardMode(
                          e.target.value as "same_as_transfer" | "keep" | "custom",
                        )
                      }
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs text-white focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="same_as_transfer">
                        Forward All Pool Numbers to Same Rep / Queue (Step 4)
                      </option>
                      <option value="keep">
                        Keep Pool's Current Forward{" "}
                        {selectedDidGroup?.action_data
                          ? `(${selectedDidGroup.action_data})`
                          : ""}
                      </option>
                      <option value="custom">
                        Forward Pool Numbers to Specific SIP URI / Phone Number...
                      </option>
                    </select>
                    {didPoolForwardMode === "custom" && (
                      <input
                        type="text"
                        value={didPoolForwardTarget}
                        onChange={(e) => setDidPoolForwardTarget(e.target.value)}
                        placeholder="e.g. 13333333336@jamal_sip or 800-555-0199"
                        className="mt-1.5 w-full rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-xs text-white placeholder-slate-500"
                      />
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-slate-300">
                      How Callbacks Connect to Your Rep:
                    </label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { id: "direct", label: "⚡ Direct to Rep" },
                        { id: "press1", label: "1️⃣ Press 1" },
                        { id: "press3", label: "3️⃣ Press 3" },
                      ].map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => {
                            setDidPoolInboundMode(
                              m.id as "direct" | "press1" | "press3",
                            );
                            setDidPoolInboundTouched(true);
                          }}
                          className={`rounded-lg px-2 py-1.5 text-[11px] font-bold transition ${
                            didPoolInboundMode === m.id
                              ? "bg-indigo-600 text-white shadow ring-1 ring-indigo-400"
                              : "bg-slate-900 text-slate-300 hover:bg-slate-800"
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dialing Speed / Concurrency Pills */}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-800/80 pt-3">
                <span className="text-xs font-medium text-slate-300">
                  Simultaneous Calls (Channels):
                </span>
                <div className="flex flex-wrap items-center gap-1.5">
                  {[10, 25, 50, 100, 250].map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => setChannel(ch)}
                      className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                        channel === ch
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                    >
                      {ch} ch
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={5000}
                    value={channel}
                    onChange={(e) =>
                      setChannel(
                        Math.max(1, Math.min(5000, Number(e.target.value) || 1)),
                      )
                    }
                    className="w-20 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-center text-xs text-white"
                  />
                </div>
              </div>
            </div>

            {/* Step 3: Audio Greeting & Keypress */}
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm text-indigo-400">🔊</span>
                <h3 className="text-sm font-semibold text-white">
                  {campaignType === "direct"
                    ? "3. Direct-to-Rep Pickup & Optional Voicemail Drop"
                    : "3. Audio Message & Caller Keypress"}
                </h3>
              </div>

              {campaignType === "direct" && (
                <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-950/25 px-3 py-2 text-xs text-emerald-200">
                  ⚡ <strong>No Press-1 Mode Active:</strong> The moment a live customer picks up the phone, they are connected straight to your rep (Step 4) with zero IVR delay.
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-300">
                    {campaignType === "direct"
                      ? "On Answering Machine / Voicemail (Optional):"
                      : "When a Live Person Answers, Play:"}
                  </label>
                  <select
                    value={recordingFile}
                    onChange={(e) => {
                      const ref = e.target.value;
                      if (ref === "__none__") {
                        setRecordingFile("__none__");
                        return;
                      }
                      const rec = inv?.recordings.find((r) => r.ref === ref);
                      const liveGreetings =
                        inv?.recordings.filter((r) => r.kind !== "voicemail") ||
                        [];
                      if (
                        rec?.kind === "voicemail" &&
                        campaignType !== "direct" &&
                        liveGreetings.length > 0
                      ) {
                        setAmRecordingFile(rec.ref);
                        const fallbackGreet =
                          liveGreetings.find((g) => g.ref === recordingFile) ||
                          liveGreetings[0];
                        setRecordingFile(fallbackGreet.ref);
                        setAutopilotNotes((prev) => [
                          `Placed voicemail recording "${rec.name}" into Answering Machine slot and kept "${fallbackGreet.name}" as your live greeting.`,
                          ...prev.slice(0, 3),
                        ]);
                        return;
                      }
                      setRecordingFile(ref);
                      if (rec?.detected_key) {
                        setConnectKey(rec.detected_key);
                        if (!didPoolInboundTouched && !selectedDidGroup?.inbound_mode) {
                          setDidPoolInboundMode(
                            rec.detected_key === "3" ? "press3" : "press1",
                          );
                        }
                      }
                      if (rec?.detected_optout_key) {
                        setOptoutKey(rec.detected_optout_key);
                      }
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                  >
                    {campaignType === "direct" ? (
                      <option value="__none__">
                        Hang Up on Voicemail — Connect Live Pickups Only (No Recording Needed)
                      </option>
                    ) : (
                      <option value="">Select an audio recording...</option>
                    )}
                    {inv?.recordings.map((r) => (
                      <option key={r.ref} value={r.ref}>
                        {r.name}
                        {r.duration_sec ? ` (${r.duration_sec}s)` : ""}
                        {r.kind === "voicemail"
                          ? " — [Voicemail / RVM]"
                          : r.detected_key
                            ? ` — [Press ${r.detected_key}${r.detected_optout_key ? ` / DNC ${r.detected_optout_key}` : ""}]`
                            : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {campaignType !== "direct" && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-300">
                      On Voicemail / Answering Machine:
                    </label>
                    <select
                      value={amRecordingFile}
                      onChange={(e) => setAmRecordingFile(e.target.value)}
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="__same__">
                        Leave Same Recording as Live Pickup
                      </option>
                      {inv?.recordings.map((r) => (
                        <option key={r.ref} value={r.ref}>
                          {r.name}
                          {r.duration_sec ? ` (${r.duration_sec}s)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {campaignType === "press1" && (
                <div className="mt-3 border-t border-slate-800/80 pt-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-slate-300">
                      Key Caller Presses to Transfer to You:
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {["1", "2", "3", "4", "5"].map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => {
                            setConnectKey(k);
                            if (optoutKey === k) {
                              setOptoutKey(k === "9" ? "8" : "9");
                            }
                            if (
                              !didPoolInboundTouched &&
                              !selectedDidGroup?.inbound_mode &&
                              (k === "1" || k === "3")
                            ) {
                              setDidPoolInboundMode(k === "3" ? "press3" : "press1");
                            }
                          }}
                          className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                            connectKey === k
                              ? "bg-emerald-600 text-white shadow ring-1 ring-emerald-400"
                              : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          }`}
                        >
                          Press {k}
                        </button>
                      ))}
                      <span className="ml-2 text-[11px] text-slate-400">
                        (Press {optoutKey} = DNC Opt-Out)
                      </span>
                    </div>
                  </div>

                  {/* Audio vs Key Mismatch Warning */}
                  {hasKeyMismatch && (
                    <div className="mt-2.5 flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 text-amber-400">⚠️</span>
                        <span>
                          Recording <strong>"{selectedRecording?.name}"</strong>{" "}
                          mentions <strong>Press {recordingDetectedKey}</strong>,
                          but Transfer Key is set to{" "}
                          <strong>Press {connectKey}</strong>!
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          recordingDetectedKey &&
                          setConnectKey(recordingDetectedKey)
                        }
                        className="shrink-0 rounded-md bg-amber-500 px-2.5 py-1 text-xs font-bold text-slate-950 hover:bg-amber-400"
                      >
                        Switch to Press {recordingDetectedKey}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 4: Where Transfers Go */}
            {(campaignType === "press1" || campaignType === "direct") && (
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-sm text-indigo-400">📲</span>
                  <h3 className="text-sm font-semibold text-white">
                    4. Where Transfers Go{" "}
                    {campaignType === "press1"
                      ? `(When Caller Presses ${connectKey})`
                      : "(On Live Pickup)"}
                  </h3>
                </div>

                {/* 4-Way Mode Tabs */}
                <div className="mb-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                  {[
                    { id: "extension", label: "Saved Destination" },
                    { id: "sip", label: "SIP Endpoint / URI" },
                    { id: "phone", label: "Phone Number" },
                    { id: "agents", label: "My Agent Queue" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setTransferMode(tab.id as TransferMode)}
                      className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                        transferMode === tab.id
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-900 text-slate-300 hover:bg-slate-800"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {transferMode === "extension" && (
                  <div>
                    <select
                      value={transferExtensionId ?? ""}
                      onChange={(e) =>
                        setTransferExtensionId(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
                    >
                      <option value="">
                        Select a saved SIP / phone transfer target...
                      </option>
                      {inv?.extensions.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name} → {x.forward_to || "No forward target"}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {transferMode === "sip" && (
                  <div>
                    <input
                      type="text"
                      value={customSipUri}
                      onChange={(e) => setCustomSipUri(e.target.value)}
                      placeholder="e.g. 13333333331@3.14.99.76 or 13333333336@jamal_sip"
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      We will automatically configure a dedicated transfer extension for this SIP endpoint when you create the campaign.
                    </p>
                  </div>
                )}

                {transferMode === "phone" && (
                  <div>
                    <input
                      type="text"
                      value={customPhone}
                      onChange={(e) => setCustomPhone(e.target.value)}
                      placeholder="e.g. (800) 555-0199 or 818-934-8477"
                      className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      Live transfers will bridge directly to this 10-digit phone number.
                    </p>
                  </div>
                )}

                {transferMode === "agents" && (
                  <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-xs text-indigo-200">
                    Callers who press <strong>{connectKey}</strong> will ring your logged-in WebRTC / SIP agents assigned to this campaign's queue.
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Non-Conversion Follow-Up SMS & Toll-Free Callback Queue Routing */}
            {(campaignType === "press1" || campaignType === "direct") && (
              <div
                className={`rounded-xl border p-4 transition ${
                  rescueEnabled
                    ? "border-emerald-500/40 bg-emerald-950/15"
                    : "border-slate-800 bg-slate-950/60"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-emerald-400">💬</span>
                    <div>
                      <h3 className="text-sm font-semibold text-white">
                        5. If They Press {connectKey} & Don't Convert → Text Toll-Free Callback Number
                      </h3>
                      <p className="text-xs text-slate-400">
                        Automatically text a Toll-Free callback number (routed to your inbound queue) if a transferred caller hangs up before converting.
                      </p>
                    </div>
                  </div>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rescueEnabled}
                      onChange={(e) => setRescueEnabled(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                    />
                    <span className="text-xs font-semibold text-emerald-300">
                      {rescueEnabled ? "Enabled" : "Off"}
                    </span>
                  </label>
                </div>

                {rescueEnabled && (
                  <div className="mt-4 space-y-3.5 border-t border-slate-800/80 pt-3.5">
                    {/* Conversion Duration Threshold */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-300">
                        Send SMS if transfer call lasts less than:
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {[
                          { sec: 120, label: "2 min (120s)" },
                          { sec: 180, label: "3 min (180s)" },
                          { sec: 300, label: "5 min (300s)" },
                          { sec: 600, label: "10 min (600s)" },
                        ].map((opt) => (
                          <button
                            key={opt.sec}
                            type="button"
                            onClick={() => setMinConvertSec(opt.sec)}
                            className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                              minConvertSec === opt.sec
                                ? "bg-emerald-600 text-white"
                                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Toll-Free Callback Number Picker + Inbound Queue Route */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-300">
                          Toll-Free Callback Number in SMS (Strictly Toll-Free Only)
                        </label>
                        <select
                          value={
                            autoBuyTollFree || !callbackDidId
                              ? "__auto_buy__"
                              : String(callbackDidId)
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "__auto_buy__") {
                              setAutoBuyTollFree(true);
                              setCallbackDidId(null);
                              setCallbackRouteMode("same_as_transfer");
                            } else {
                              const id = Number(val);
                              setAutoBuyTollFree(false);
                              setCallbackDidId(id);
                              const found = inv?.callback_dids.find(
                                (d) => d.id === id,
                              );
                              if (found) {
                                setRescueSmsText((prev) =>
                                  replacePhoneInSms(prev, found.formatted),
                                );
                              }
                            }
                          }}
                          className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white focus:border-emerald-500 focus:outline-none"
                        >
                          {inv?.callback_dids.map((d, idx) => (
                            <option key={d.id} value={d.id}>
                              {idx === 0 ? "★ Recommended: " : ""}
                              {d.formatted} (Toll-Free){" "}
                              {d.action_data
                                ? `→ ${d.action_type}: ${d.action_data}`
                                : ""}
                            </option>
                          ))}
                          <option value="__auto_buy__">
                            ➕ Auto-Buy & Forward a New Toll-Free Number (1-833 / 1-888)
                          </option>
                        </select>

                        {(autoBuyTollFree || !callbackDidId) && (
                          <div className="mt-1.5 rounded-lg border border-emerald-500/30 bg-emerald-950/30 px-2.5 py-1.5 text-[11px] text-emerald-200">
                            ✨ Will automatically purchase a fresh Toll-Free number via Bandwidth, forward it to your inbound queue, and inject it into the SMS on campaign creation.
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-medium text-slate-300">
                          When They Call That Toll-Free Number Back:
                        </label>
                        <select
                          value={callbackRouteMode}
                          onChange={(e) =>
                            setCallbackRouteMode(
                              e.target.value as
                                | "same_as_transfer"
                                | "keep"
                                | "inbound_queue"
                                | "custom",
                            )
                          }
                          className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white focus:border-emerald-500 focus:outline-none"
                        >
                          <option value="same_as_transfer">
                            Route to Same Inbound Queue / Transfer Target (Step 4)
                          </option>
                          <option value="keep">
                            Keep Number's Current Inbound Route{" "}
                            {selectedCallbackDid?.action_data
                              ? `(${selectedCallbackDid.action_data})`
                              : ""}
                          </option>
                          <option value="inbound_queue">
                            Route to My Campaign WebRTC Agent Queue
                          </option>
                          <option value="custom">
                            Route to Specific Inbound Queue SIP URI / Number...
                          </option>
                        </select>

                        {callbackRouteMode === "custom" && (
                          <input
                            type="text"
                            value={callbackCustomRouteTarget}
                            onChange={(e) =>
                              setCallbackCustomRouteTarget(e.target.value)
                            }
                            placeholder="e.g. 13333333336@jamal_sip or 800-555-0199"
                            className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs text-white placeholder-slate-500"
                          />
                        )}
                      </div>
                    </div>

                    {/* 1-Click SMS Templates + Textarea */}
                    <div>
                      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                        <label className="text-xs font-medium text-slate-300">
                          SMS Message Sent on Non-Converting Transfer:
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-slate-400">
                            1-Click Presets:
                          </span>
                          {[
                            { id: "queue", label: "Inbound Queue" },
                            { id: "debt", label: "Debt Relief Desk" },
                            { id: "tax", label: "Tax Specialist" },
                          ].map((tpl) => (
                            <button
                              key={tpl.id}
                              type="button"
                              onClick={() =>
                                setRescueSmsText(
                                  buildSmsTemplate(
                                    tpl.id as "queue" | "debt" | "tax",
                                    selectedCallbackPhone || "1-888-555-0199",
                                  ),
                                )
                              }
                              className="rounded border border-slate-700 bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-slate-200 hover:bg-slate-700"
                            >
                              {tpl.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        rows={3}
                        value={rescueSmsText}
                        onChange={(e) => setRescueSmsText(e.target.value)}
                        className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2.5 text-xs leading-relaxed text-white focus:border-emerald-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Optional Instant SMS on Keypress (shown when Recipe 3 is selected or toggled) */}
            {campaignType === "press1" && instantSmsEnabled && (
              <div className="rounded-xl border border-indigo-500/40 bg-indigo-950/15 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-300">
                    Instant Text Sent Immediately When Caller Presses{" "}
                    {connectKey}:
                  </span>
                  <button
                    type="button"
                    onClick={() => setInstantSmsEnabled(false)}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    Turn Off
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={instantSmsText}
                  onChange={(e) => setInstantSmsText(e.target.value)}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 p-2.5 text-xs text-white"
                />
              </div>
            )}

            {/* Campaign Name */}
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <label className="text-xs font-semibold text-slate-300">
                  Campaign Name:
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameTouched(true);
                  }}
                  className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm font-medium text-white focus:border-indigo-500 focus:outline-none sm:max-w-md"
                />
              </div>
            </div>
          </div>

          {/* Right Column: Live Visual Call & SMS Flowchart + 1-Click Create */}
          <div className="flex flex-col justify-between border-t border-slate-800 bg-slate-950/90 p-6 lg:col-span-5 lg:border-l lg:border-t-0">
            <div className="space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-indigo-400">
                  Live Campaign Flowchart
                </h3>
                <p className="text-xs text-slate-400">
                  Here is the exact path every call will follow when you start this campaign:
                </p>
              </div>

              {autopilotNotes.length > 0 && (
                <div className="rounded-xl border border-indigo-500/30 bg-indigo-950/30 p-3 text-xs text-indigo-200">
                  <div className="mb-1 flex items-center gap-1.5 font-semibold text-indigo-300">
                    <span>🛡️</span>
                    Self-Healing Autopilot Pre-Loaded Your Proven Setup:
                  </div>
                  <ul className="list-inside list-disc space-y-0.5 text-[11px] text-indigo-100/90">
                    {autopilotNotes.slice(0, 4).map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Flowchart Nodes */}
              <div className="space-y-2.5">
                {/* Node 1: Outbound Dial & Caller ID Pool Callback Routing */}
                <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-indigo-300">
                    <span>📞</span>
                    STEP 1 — DIAL LIST & CALLER ID POOL CALLBACKS
                  </div>
                  <p className="mt-1 text-xs text-slate-200">
                    Dial{" "}
                    <strong className="text-white">
                      {selectedList
                        ? `${selectedList.name} (${selectedList.count.toLocaleString()} leads)`
                        : "Select a Contact List"}
                    </strong>{" "}
                    using{" "}
                    <strong className="text-white">
                      {selectedDidGroup
                        ? `${selectedDidGroup.name} (${selectedDidGroup.count} DIDs)`
                        : "Select a DID Pool"}
                    </strong>{" "}
                    at <strong className="text-white">{channel} channels</strong>.
                  </p>
                  {selectedDidGroup && (
                    <p className="mt-1 text-[11px] text-indigo-200/90">
                      📲 If someone calls back any of your{" "}
                      <strong>{selectedDidGroup.count}</strong> Caller IDs:{" "}
                      <strong>
                        {didPoolForwardMode === "same_as_transfer"
                          ? `Forward to ${transferSummaryDisplay}`
                          : didPoolForwardMode === "custom"
                            ? `Forward to ${didPoolForwardTarget || "Custom Target"}`
                            : `Keep current forward (${selectedDidGroup.action_data || "configured"})`}
                      </strong>{" "}
                      via{" "}
                      <strong className="text-emerald-300">
                        {didPoolInboundMode === "direct"
                          ? "Direct to Rep (No Keypress)"
                          : didPoolInboundMode === "press3"
                            ? "Press 3 IVR"
                            : "Press 1 IVR"}
                      </strong>
                      .
                    </p>
                  )}
                </div>

                {/* Node 2: On Live Pickup */}
                {campaignType !== "direct" && (
                  <div className="rounded-xl border border-slate-800 bg-slate-900/90 p-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-sky-300">
                      <span>🔊</span>
                      STEP 2 — WHEN A PERSON ANSWERS
                    </div>
                    <p className="mt-1 text-xs text-slate-200">
                      Play{" "}
                      <strong className="text-white">
                        {selectedRecording?.name || "Select a Recording"}
                      </strong>
                      {selectedRecording?.duration_sec
                        ? ` (${selectedRecording.duration_sec}s)`
                        : ""}{" "}
                      • Voicemail:{" "}
                      <strong className="text-slate-300">
                        {amRecordingFile === "__same__"
                          ? "Same Greeting"
                          : inv?.recordings.find(
                              (r) => r.ref === amRecordingFile,
                            )?.name || "Custom"}
                      </strong>
                    </p>
                  </div>
                )}

                {/* Node 3: On Press X Transfer or Direct Live Connect */}
                {(campaignType === "press1" || campaignType === "direct") && (
                  <div className="rounded-xl border border-indigo-500/30 bg-indigo-950/20 p-3">
                    <div className="flex items-center gap-2 text-xs font-bold text-indigo-300">
                      <span>📲</span>
                      {campaignType === "press1"
                        ? `STEP 3 — WHEN THEY PRESS ${connectKey}`
                        : "STEP 2 — WHEN A LIVE PERSON ANSWERS (NO PRESS-1)"}
                    </div>
                    <p className="mt-1 text-xs text-slate-200">
                      {campaignType === "direct"
                        ? "Connect caller immediately (no keypress) to "
                        : "Transfer immediately to "}
                      <strong className="text-white">
                        {transferSummaryDisplay}
                      </strong>
                      {campaignType === "press1" ? (
                        <span className="block text-[11px] text-slate-400">
                          (If they press {optoutKey}, add to DNC & hang up)
                        </span>
                      ) : (
                        <span className="block text-[11px] text-slate-400">
                          Voicemail:{" "}
                          {!recordingFile || recordingFile === "__none__"
                            ? "Hang up on answering machines (Live Pickups Only)"
                            : `Drop "${selectedRecording?.name || recordingFile}"`}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {/* Node 4: If They Don't Convert -> Rescue SMS + Toll-Free Queue */}
                {(campaignType === "press1" || campaignType === "direct") &&
                  rescueEnabled && (
                    <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/20 p-3">
                      <div className="flex items-center gap-2 text-xs font-bold text-emerald-300">
                        <span>💬</span>
                        STEP 4 — IF TRANSFER DOESN'T CONVERT (&lt;{" "}
                        {Math.round(minConvertSec / 60)} MIN)
                      </div>
                      <p className="mt-1 text-xs text-slate-200">
                        Automatically text them Toll-Free callback{" "}
                        <strong className="text-emerald-300">
                          {selectedCallbackPhone || "Selected Number"}
                        </strong>
                        .
                      </p>
                      <p className="mt-1 text-[11px] text-emerald-200/80">
                        Inbound calls to{" "}
                        <strong>
                          {selectedCallbackPhone || "that Toll-Free number"}
                        </strong>{" "}
                        will:{" "}
                        <strong>
                          {callbackRouteMode === "same_as_transfer"
                            ? `Route to ${transferSummaryDisplay}`
                            : callbackRouteMode === "inbound_queue"
                              ? "Ring My Campaign Agent Queue"
                              : callbackRouteMode === "custom"
                                ? `Route to ${callbackCustomRouteTarget || "Custom Queue"}`
                                : `Keep current route (${selectedCallbackDid?.action_data || "configured"})`}
                        </strong>{" "}
                        (Protected from release).
                      </p>
                    </div>
                  )}
              </div>

              {/* Validation Status */}
              {validationErrors.length > 0 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                  <div className="mb-1 flex items-center gap-1.5 font-semibold text-amber-300">
                    <span>⚠️</span>
                    Almost ready — complete these items:
                  </div>
                  <ul className="list-inside list-disc space-y-0.5 text-amber-100/90">
                    {validationErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-medium text-emerald-200">
                  <span className="shrink-0 text-emerald-400">✅</span>
                  All steps verified! Campaign will be created in safe Pending mode so you can start it whenever you're ready.
                </div>
              )}

              {commitMut.isError && (
                <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-200">
                  {(commitMut.error as Error)?.message ||
                    "Failed to create campaign. Please check your selections."}
                </div>
              )}
            </div>

            {/* Footer Action Button */}
            <div className="mt-4 border-t border-slate-800 pt-4">
              <button
                type="button"
                disabled={validationErrors.length > 0 || commitMut.isPending}
                onClick={() => commitMut.mutate()}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-950/50 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {commitMut.isPending ? (
                  <span>⏳ Creating Campaign & Configuring Routing...</span>
                ) : (
                  <span>🛡️ Create Campaign (Safe Pending Mode) →</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
